import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import { orderGroupIdZodSchema } from "@@/shared/order-groups/order-group-id";
import { payRequestItemZodObject } from "@@/shared/pay-requests/pay-request-item";
import { payRequestIdZodSchema } from "@@/shared/pay-requests/pay-request-id";
import { priceZodSchema } from "@@/shared/prices/price";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { z } from "zod";
import { orderIdZodSchema } from "../../order-id";
import { financialOrderInformationForCustomerEventIdZodSchema } from "./financial-order-information-for-customer-event-id";
import { orderEventIdZodSchema } from "../../order-events/events/_order-event-id";
import { paymentProviderZodSchema } from "@@/shared/payments/payment-providers";

const payRequestReceiptForCustomerZodObject = z.object({
    _id: financialOrderInformationForCustomerEventIdZodSchema,
    _type: z.literal("FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT"),
    _createdAt: isoAndUnixZodSchema,
    type: z.literal("PAY_REQUEST_PAYMENT"),
    payRequestId: payRequestIdZodSchema,
    status: z.enum(["PENDING", "CANCELLED", "FULFILLED"]),
    amount: priceZodSchema,
    items: z.array(payRequestItemZodObject),
    signature: z.string(),
    originTypes: z.array(z.enum(["ORDER_CREATED", "ORDER_CHANGED"])),
    receiptFor: z.object({
        orderGroupId: orderGroupIdZodSchema,
        orderId: orderIdZodSchema,
        providerId: providerIdZodSchema,
        orderEventIds: z.array(orderEventIdZodSchema),
    }),
});
type PayRequestReceiptForCustomer = z.infer<
    typeof payRequestReceiptForCustomerZodObject
>;

const onPremiseReceiptForCustomerZodObject = z.object({
    _id: financialOrderInformationForCustomerEventIdZodSchema,
    _type: z.literal("FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT"),
    _createdAt: isoAndUnixZodSchema,
    type: z.literal("ON_PREMISE_PAYMENT"),
    status: z.enum(["PENDING", "CANCELLED", "FULFILLED"]),
    amount: priceZodSchema,
    signature: z.string(),
    items: z.array(payRequestItemZodObject),
    originTypes: z.array(z.enum(["ORDER_CREATED", "ORDER_CHANGED"])),
    receiptFor: z.object({
        orderGroupId: orderGroupIdZodSchema,
        orderId: orderIdZodSchema,
        providerId: providerIdZodSchema,
        orderEventIds: z.array(orderEventIdZodSchema),
    }),
});
type OnPremiseReceiptForCustomer = z.infer<
    typeof onPremiseReceiptForCustomerZodObject
>;

const waivedPaymentReceiptForCustomerZodObject = z.object({
    _id: financialOrderInformationForCustomerEventIdZodSchema,
    _type: z.literal("FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT"),
    _createdAt: isoAndUnixZodSchema,
    type: z.literal("WAIVED_PAYMENT"),
    status: z.enum(["PENDING", "CANCELLED", "FULFILLED"]),
    amount: priceZodSchema,
    signature: z.string(),
    items: z.array(payRequestItemZodObject),
    originTypes: z.array(z.enum(["ORDER_CREATED", "ORDER_CHANGED"])),
    receiptFor: z.object({
        orderGroupId: orderGroupIdZodSchema,
        orderId: orderIdZodSchema,
        providerId: providerIdZodSchema,
        orderEventIds: z.array(orderEventIdZodSchema),
    }),
});
type WaivedReceiptForCustomer = z.infer<
    typeof waivedPaymentReceiptForCustomerZodObject
>;

const paymentReceiptForCustomerZodObject = z.object({
    _id: financialOrderInformationForCustomerEventIdZodSchema,
    _type: z.literal("FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT"),
    _createdAt: isoAndUnixZodSchema,
    type: z.literal("PAYMENT"),
    status: z.enum(["PENDING", "CANCELLED", "FULFILLED"]),
    amount: priceZodSchema,
    signature: z.string(),
    items: z.array(payRequestItemZodObject),
    originTypes: z.array(z.enum(["ORDER_CREATED", "ORDER_CHANGED"])),
    receiptFor: z.object({
        orderGroupId: orderGroupIdZodSchema,
        orderId: orderIdZodSchema,
        providerId: providerIdZodSchema,
        orderEventIds: z.array(orderEventIdZodSchema),
    }),
});
type PaymentReceiptForCustomer = z.infer<
    typeof paymentReceiptForCustomerZodObject
>;

const refundTransactionStatusZodObject = z.object({
    _type: z.literal("REFUND_TRANSACTION_STATUS"),
    _createdAt: isoAndUnixZodSchema,
    amount: priceZodSchema,
    status: z.enum(["PENDING", "CANCELLED", "FULFILLED"]),
    paymentProvider: paymentProviderZodSchema,
    signature: z.string(),
});
type RefundTransactionStatus = z.infer<typeof refundTransactionStatusZodObject>;
const isRefundTransactionStatus = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is RefundTransactionStatus => {
    return event?._type === "REFUND_TRANSACTION_STATUS";
};

const refundReceiptForCustomerZodObject = z.object({
    _id: financialOrderInformationForCustomerEventIdZodSchema,
    _type: z.literal("FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT"),
    _createdAt: isoAndUnixZodSchema,
    type: z.literal("REFUND"),
    status: z.enum(["PENDING", "CANCELLED", "FULFILLED"]),
    amount: priceZodSchema,
    reasonType: z.enum(["FULL", "PARTIAL", "ORDER_CHANGE"]),
    signature: z.string(),
    items: z.array(payRequestItemZodObject),
    originTypes: z.array(z.enum(["ORDER_CREATED", "ORDER_CHANGED"])),
    receiptFor: z.object({
        orderGroupId: orderGroupIdZodSchema,
        orderId: orderIdZodSchema,
        providerId: providerIdZodSchema,
        orderEventIds: z.array(orderEventIdZodSchema),
    }),
    refunds: z.array(refundTransactionStatusZodObject),
});
type RefundReceiptForCustomer = z.infer<
    typeof refundReceiptForCustomerZodObject
>;
const isRefundReceiptForCustomer = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is RefundReceiptForCustomer => {
    return (
        event?._type === "FINANCIAL_ORDER_HISTORY_FOR_CUSTOMER_EVENT" &&
        event?.type === "REFUND"
    );
};

const receiptForCustomerZodObject = z.union([
    payRequestReceiptForCustomerZodObject,
    onPremiseReceiptForCustomerZodObject,
    paymentReceiptForCustomerZodObject,
    refundReceiptForCustomerZodObject,
    waivedPaymentReceiptForCustomerZodObject,
]);

type ReceiptForCustomer = z.infer<typeof receiptForCustomerZodObject>;

export {
    isRefundReceiptForCustomer,
    isRefundTransactionStatus,
    receiptForCustomerZodObject,
};
export type {
    OnPremiseReceiptForCustomer,
    PayRequestReceiptForCustomer,
    PaymentReceiptForCustomer,
    ReceiptForCustomer,
    RefundReceiptForCustomer,
    RefundTransactionStatus,
    WaivedReceiptForCustomer,
};
